import { ArchivioPezzi, chiavePezzo, chiaveSegnaposto, StatoRegistrazioneLocale } from './verbaliArchivioPezzi';
import { RiepilogoAudio, terminaAudio } from './verbaliAudioService';
import { CodaInvio } from './verbaliInvioPezzi';

// Registratore del browser per l'audio del verbale (piano 2): MediaRecorder in blocchi da 60 s,
// ognuno messo SUBITO nella coda di invio (che lo salva nell'archivio locale prima di spedirlo).
// Lo stato della registrazione (audio, ripresa, blocchi creati) si salva nell'archivio a ogni
// blocco, così riaprendo la pagina dopo una scheda chiusa si vede «Registrazione interrotta».
// Quando la scheda va in secondo piano si chiede subito il blocco in corso (`requestData`): se il
// browser la chiude, al più si perde l'audio degli ultimi istanti.

export const TIMESLICE_MS = 60000;
export const BITRATE_AUDIO = 48000;
/** Lunghezza massima dell'etichetta di un momento segnato (come il server). */
export const MAX_ETICHETTA_MOMENTO = 120;
/** Attesa massima dell'evento `stop` del MediaRecorder dopo `stop()`. */
export const ATTESA_STOP_MS = 10000;
/** Scheda nascosta: si chiede il blocco in corso solo se l'ultimo è più vecchio di così (niente blocchi minuscoli). */
export const MIN_MS_RICHIESTA_DATI = 10000;

export const MESSAGGIO_BLOCCHI_NON_INVIATI =
  'Alcuni blocchi della registrazione non sono ancora arrivati al server: restano su questo computer, riprova quando torna la rete';

export type EstensioneAudio = 'webm' | 'ogg' | 'm4a' | '';
export type StatoRegistratore = 'pronto' | 'registra' | 'pausa' | 'fermo';

const FORMATI: { mimeType: string; estensione: EstensioneAudio }[] = [
  { mimeType: 'audio/webm;codecs=opus', estensione: 'webm' },
  { mimeType: 'audio/ogg;codecs=opus', estensione: 'ogg' },
  { mimeType: 'audio/mp4', estensione: 'm4a' },
];

/** Primo formato supportato dal browser; `''` = predefinito del browser. */
export function scegliFormato(supporta: (mime: string) => boolean): { mimeType: string; estensione: EstensioneAudio } {
  for (const f of FORMATI) {
    try {
      if (supporta(f.mimeType)) return { ...f };
    } catch { /* isTypeSupported guasto: si prova il successivo */ }
  }
  return { mimeType: '', estensione: '' };
}

/** La parte di MediaRecorder che usa il registratore (finta nei test). */
export interface MediaRecorderSimile {
  readonly state: 'inactive' | 'recording' | 'paused';
  start(timeslice?: number): void;
  stop(): void;
  pause(): void;
  resume(): void;
  requestData(): void;
  addEventListener(tipo: string, ascoltatore: (e: any) => void): void;
  removeEventListener(tipo: string, ascoltatore: (e: any) => void): void;
}

export interface DocumentoSimile {
  readonly visibilityState: string;
  addEventListener(tipo: string, ascoltatore: () => void): void;
  removeEventListener(tipo: string, ascoltatore: () => void): void;
}

export interface AmbienteRegistratore {
  creaMediaRecorder(stream: MediaStream, opzioni: { mimeType: string; audioBitsPerSecond: number }): MediaRecorderSimile;
  ora: () => number;
  /** Per `visibilitychange` (predefinito: `document`). */
  documento?: DocumentoSimile | null;
  attesaStopMs?: number;
}

/** Stato locale salvato nell'archivio; `terminaRichiesta` = «Termina» premuto, chiusura non completata. */
export interface StatoLocaleVerbale extends StatoRegistrazioneLocale {
  terminaRichiesta?: boolean;
}

export class ErroreRegistrazione extends Error {
  codice: string;
  constructor(codice: string, messaggio: string) {
    super(messaggio);
    this.name = 'ErroreRegistrazione';
    this.codice = codice;
  }
}

export interface Registratore {
  avvia(stream: MediaStream): void;
  pausa(): void;
  riprendi(): void;
  segnaMomento(etichetta: string): void;
  /** Ferma, attende l'ultimo blocco, invia tutto e chiude l'audio sul server (⇒ audio aggiornato). */
  termina(): Promise<RiepilogoAudio | null>;
  /** Ferma senza chiudere l'audio (pagina lasciata): blocchi e stato restano nell'archivio. */
  interrompi(): void;
  /** Millisecondi registrati in questa ripresa, pause escluse. */
  msRipresa(): number;
  /** Blocchi creati in questa ripresa. */
  pezzi(): number;
  /** Ora (di `ambiente.ora`) dell'ultimo blocco, o dell'avvio se non ce ne sono. */
  ultimoPezzoAt(): number;
  readonly stato: StatoRegistratore;
  readonly audioId: string;
  readonly ripresa: number;
  onCambio(cb: (stato: StatoRegistratore) => void): () => void;
}

export interface OpzioniRegistratore {
  eventId: string;
  audioId: string;
  ripresa: number;
  archivio: ArchivioPezzi;
  coda: CodaInvio;
  ambiente: AmbienteRegistratore;
  /** Da `scegliFormato` (`''` = predefinito del browser). */
  mimeType?: string;
}

/** Ambiente reale: MediaRecorder del browser, `Date.now`, `document`. */
export function ambienteRegistratoreBrowser(): AmbienteRegistratore {
  return {
    creaMediaRecorder(stream, { mimeType, audioBitsPerSecond }) {
      const opzioni: MediaRecorderOptions = { audioBitsPerSecond };
      if (mimeType) opzioni.mimeType = mimeType;
      return new MediaRecorder(stream, opzioni) as unknown as MediaRecorderSimile;
    },
    ora: () => Date.now(),
    documento: typeof document !== 'undefined' ? document : null,
  };
}

export function creaRegistratore({
  eventId, audioId, ripresa, archivio, coda, ambiente, mimeType = '',
}: OpzioniRegistratore): Registratore {
  const { ora } = ambiente;
  const documento: DocumentoSimile | null = ambiente.documento !== undefined
    ? ambiente.documento
    : (typeof document !== 'undefined' ? document : null);
  const attesaStopMs = ambiente.attesaStopMs ?? ATTESA_STOP_MS;

  let stato: StatoRegistratore = 'pronto';
  let recorder: MediaRecorderSimile | null = null;
  let n = 0;
  let accumulato = 0;
  let inizioTratto: number | null = null;
  let iniziataAt = 0;
  let ultimoPezzoAt = 0;
  let ultimoSegnaposto = 0;
  let terminaRichiesta = false;
  let fermoVoluto = false;
  /** Dopo la fase di stop di «Termina» il numero di blocchi è dichiarato: i ritardatari si ignorano. */
  let blocchiChiusi = false;
  let recorderFermo = false;
  let fineStop: (() => void) | null = null;
  let promessaTermina: Promise<RiepilogoAudio | null> | null = null;
  // Blocchi, segnaposti e salvataggi dello stato in fila: l'ordine è quello di creazione.
  let catena: Promise<void> = Promise.resolve();
  const ascoltatori = new Set<(s: StatoRegistratore) => void>();

  const inFila = (lavoro: () => Promise<void>) => {
    catena = catena.then(lavoro).catch(() => undefined);
    return catena;
  };

  const imposta = (s: StatoRegistratore) => {
    if (stato === s) return;
    stato = s;
    ascoltatori.forEach(cb => { try { cb(s); } catch { /* ascoltatore guasto */ } });
  };

  const msRipresa = () => accumulato + (inizioTratto !== null ? Math.max(0, ora() - inizioTratto) : 0);

  const chiudiTratto = () => {
    if (inizioTratto !== null) {
      accumulato += Math.max(0, ora() - inizioTratto);
      inizioTratto = null;
    }
  };

  const statoLocale = (pezziCreati: number): StatoLocaleVerbale => ({
    audioId,
    ripresa,
    pezziCreati,
    msRipresa: Math.round(msRipresa()),
    iniziataAt,
    ultimoPezzoAt,
    inPausa: stato === 'pausa',
    ...(terminaRichiesta ? { terminaRichiesta: true } : {}),
  });

  /** Salva lo stato in fila (dopo i blocchi già creati); un archivio guasto non ferma la registrazione. */
  const salvaStato = (pezziCreati = n) => {
    const s = statoLocale(pezziCreati);
    return inFila(async () => {
      try { await archivio.salvaStato(eventId, s); } catch { /* lo stato si ricostruisce dal server */ }
    });
  };

  function alDato(e: any) {
    const blob: Blob | undefined = e?.data;
    if (!blob || !blob.size) return;
    if (blocchiChiusi) {
      // Arrivato dopo l'attesa massima dello stop: contarlo renderebbe sbagliato il numero dichiarato.
      console.warn(`Verbali: blocco arrivato dopo la chiusura della registrazione ignorato (${blob.size} byte)`);
      return;
    }
    const indice = n++;
    const creatoAt = ora();
    ultimoPezzoAt = creatoAt;
    inFila(() => coda.aggiungi({
      chiave: chiavePezzo(audioId, ripresa, indice),
      eventId,
      audioId,
      tipo: 'pezzo',
      ripresa,
      indice,
      blob,
      creatoAt,
    }));
    salvaStato(indice + 1);
  }

  function alVisibilita() {
    if (!documento || documento.visibilityState !== 'hidden') return;
    if (recorder && recorder.state !== 'inactive' && ora() - ultimoPezzoAt >= MIN_MS_RICHIESTA_DATI) {
      try { recorder.requestData(); } catch { /* recorder già fermo */ }
    }
  }

  function staccaDocumento() {
    documento?.removeEventListener('visibilitychange', alVisibilita);
  }

  function alStop() {
    recorderFermo = true;
    if (fineStop) fineStop();
    if (fermoVoluto) return;
    // Fermato dal browser (microfono scollegato, errore): blocchi e stato restano per la ripresa.
    chiudiTratto();
    staccaDocumento();
    salvaStato();
    imposta('fermo');
  }

  function alErrore() {
    if (recorder && recorder.state === 'inactive' && !recorderFermo) alStop();
  }

  /** `stop()` e attesa dell'evento `stop` (l'ultimo `dataavailable` arriva prima). */
  function fermaRecorder(): Promise<void> {
    if (!recorder || recorderFermo || recorder.state === 'inactive') return Promise.resolve();
    const r = recorder;
    return new Promise<void>(resolve => {
      const timer = setTimeout(fine, attesaStopMs);
      function fine() {
        clearTimeout(timer);
        fineStop = null;
        resolve();
      }
      fineStop = fine;
      try { r.stop(); } catch { fine(); }
    });
  }

  const registratore: Registratore = {
    get stato() { return stato; },
    audioId,
    ripresa,

    avvia(stream) {
      if (stato !== 'pronto') throw new Error('Registrazione già avviata');
      const r = ambiente.creaMediaRecorder(stream, { mimeType, audioBitsPerSecond: BITRATE_AUDIO });
      r.addEventListener('dataavailable', alDato);
      r.addEventListener('stop', alStop);
      r.addEventListener('error', alErrore);
      recorder = r;
      r.start(TIMESLICE_MS);
      iniziataAt = ora();
      inizioTratto = iniziataAt;
      ultimoPezzoAt = iniziataAt;
      documento?.addEventListener('visibilitychange', alVisibilita);
      imposta('registra');
      salvaStato();
    },

    pausa() {
      if (stato !== 'registra' || !recorder) return;
      try { recorder.pause(); } catch { return; }
      chiudiTratto();
      imposta('pausa');
      salvaStato();
    },

    riprendi() {
      if (stato !== 'pausa' || !recorder) return;
      try { recorder.resume(); } catch { return; }
      inizioTratto = ora();
      imposta('registra');
      salvaStato();
    },

    segnaMomento(etichetta) {
      if (stato !== 'registra' && stato !== 'pausa') return;
      const testo = String(etichetta || '').trim().slice(0, MAX_ETICHETTA_MOMENTO);
      if (!testo) return;
      // `creatoAt` fa parte della chiave: due momenti nello stesso millisecondo restano distinti.
      let creatoAt = ora();
      if (creatoAt <= ultimoSegnaposto) creatoAt = ultimoSegnaposto + 1;
      ultimoSegnaposto = creatoAt;
      const ms = Math.round(msRipresa());
      inFila(() => coda.aggiungi({
        chiave: chiaveSegnaposto(audioId, creatoAt),
        eventId,
        audioId,
        tipo: 'segnaposto',
        ripresa,
        msRipresa: ms,
        etichetta: testo,
        creatoAt,
      }));
    },

    termina() {
      if (promessaTermina) return promessaTermina;
      if (stato === 'pronto') return Promise.reject(new Error('Registrazione non avviata'));
      promessaTermina = (async () => {
        terminaRichiesta = true;
        fermoVoluto = true;
        chiudiTratto();
        await fermaRecorder();
        blocchiChiusi = true;
        staccaDocumento();
        imposta('fermo');
        // Lo stato con `terminaRichiesta`: se la pagina si chiude prima della fine, riaprendola la
        // chiusura riprende da sola.
        salvaStato();
        await catena;
        await coda.svuota();
        // Blocchi di QUESTO audio ancora nell'archivio, o coda chiusa (pagina lasciata) con invii in sospeso.
        let rimasti = 0;
        try {
          rimasti = (await archivio.elenca(eventId)).filter(p => p.tipo === 'pezzo' && p.audioId === audioId).length;
        } catch { rimasti = 0; }
        if (rimasti > 0 || (coda.inAttesa() > 0 && !coda.fermo())) {
          throw new ErroreRegistrazione('BLOCCHI_NON_INVIATI', MESSAGGIO_BLOCCHI_NON_INVIATI);
        }
        const { audio } = await terminaAudio(eventId, audioId, { riprese: [{ indice: ripresa, pezzi: n }] });
        try { await archivio.salvaStato(eventId, null); } catch { /* riaprendo, «Termina» risponde 200 */ }
        return audio || null;
      })();
      promessaTermina.catch(() => { promessaTermina = null; });
      return promessaTermina;
    },

    interrompi() {
      if (stato !== 'registra' && stato !== 'pausa') return;
      fermoVoluto = true;
      chiudiTratto();
      staccaDocumento();
      // L'ultimo `dataavailable` arriva dopo: il blocco va comunque nell'archivio.
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* già fermo */ }
      }
      imposta('fermo');
      salvaStato();
    },

    msRipresa,
    pezzi: () => n,
    ultimoPezzoAt: () => ultimoPezzoAt,

    onCambio(cb) {
      ascoltatori.add(cb);
      return () => { ascoltatori.delete(cb); };
    },
  };

  return registratore;
}

// ========== Lucchetto della registrazione (una scheda per evento) ==========

/**
 * Archivio e stato locale sono dell'origine (comuni a tutte le schede): mentre una scheda registra
 * o chiude un audio tiene il lucchetto dell'evento, così un'altra scheda non mostra «interrotta»,
 * non riprende con un secondo microfono e non chiude l'audio sotto i piedi di chi registra.
 */
export interface LucchettoRegistrazione {
  /** Prende il lucchetto (null = lo tiene un'altra scheda); la funzione restituita lo rilascia. */
  prendi(nome: string): Promise<(() => void) | null>;
  /** true se qualcuno lo tiene. */
  occupato(nome: string): Promise<boolean>;
}

export const nomeLucchetto = (eventId: string) => `verbale-reg:${eventId}`;

/** La parte di `navigator.locks` (Web Locks) che serve. */
export interface LocksSimile {
  request(nome: string, opzioni: { ifAvailable: boolean }, cb: (lock: unknown) => unknown): Promise<unknown>;
  query(): Promise<{ held?: Array<{ name?: string }> }>;
}

/**
 * Web Locks: il lucchetto resta preso finché non si rilascia o la scheda si chiude (anche per un
 * crash: lo libera il browser). Se l'API si guasta si lascia registrare (meglio due schede che nessuna).
 */
export function lucchettoWebLocks(locks: LocksSimile): LucchettoRegistrazione {
  return {
    prendi(nome) {
      return new Promise(resolve => {
        let deciso = false;
        let rilascia: () => void = () => undefined;
        const tenuto = new Promise<void>(r => { rilascia = r; });
        locks.request(nome, { ifAvailable: true }, lock => {
          deciso = true;
          if (!lock) {
            resolve(null);
            return undefined;
          }
          resolve(() => rilascia());
          return tenuto;
        }).catch(() => {
          if (!deciso) {
            deciso = true;
            resolve(() => undefined);
          }
        });
      });
    },
    async occupato(nome) {
      try {
        const stato = await locks.query();
        return (stato.held || []).some(l => l.name === nome);
      } catch {
        return false;
      }
    },
  };
}

export const BATTITO_LUCCHETTO_MS = 5000;
/** Ampio: una scheda in secondo piano può avere i timer rallentati fino a un minuto. */
export const SCADENZA_LUCCHETTO_MS = 90000;

/** Ripiego senza Web Locks: battito in localStorage (una scheda chiusa male libera dopo 90 s). */
export function lucchettoStorage(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  ora: () => number = () => Date.now(),
  { battitoMs = BATTITO_LUCCHETTO_MS, scadenzaMs = SCADENZA_LUCCHETTO_MS } = {}
): LucchettoRegistrazione {
  const id = `${ora()}-${Math.random().toString(36).slice(2)}`;
  const chiave = (nome: string) => `unasca:${nome}`;
  const leggi = (nome: string): { id: string; at: number } | null => {
    try {
      const v = JSON.parse(storage.getItem(chiave(nome)) || 'null');
      return v && typeof v.id === 'string' && typeof v.at === 'number' ? v : null;
    } catch {
      return null;
    }
  };
  const vivo = (v: { at: number } | null) => !!v && ora() - v.at < scadenzaMs;
  return {
    async prendi(nome) {
      const v = leggi(nome);
      if (v && vivo(v) && v.id !== id) return null;
      const scrivi = () => {
        try { storage.setItem(chiave(nome), JSON.stringify({ id, at: ora() })); } catch { /* storage pieno */ }
      };
      scrivi();
      const timer = setInterval(scrivi, battitoMs);
      return () => {
        clearInterval(timer);
        if (leggi(nome)?.id === id) {
          try { storage.removeItem(chiave(nome)); } catch { /* storage non disponibile */ }
        }
      };
    },
    async occupato(nome) {
      return vivo(leggi(nome));
    },
  };
}

/** Web Locks se ci sono, altrimenti localStorage, altrimenti nessun lucchetto. */
export function lucchettoBrowser(): LucchettoRegistrazione {
  const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
  if (nav?.locks && typeof nav.locks.request === 'function' && typeof nav.locks.query === 'function') {
    return lucchettoWebLocks(nav.locks);
  }
  try {
    if (typeof localStorage !== 'undefined') return lucchettoStorage(localStorage);
  } catch { /* localStorage negato */ }
  return { prendi: async () => () => undefined, occupato: async () => false };
}
